import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/movies';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus(null);

    const formEl = e.currentTarget as HTMLFormElement;
    const honeypot = formEl.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus({ type: 'success', message: '留言已提交，我们会尽快回复您！' });
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData(formEl);
      if (honeypot) formData.delete('website_alt');

      const response = await fetch('https://readdy.ai/api/form/d9nfhscit35ii3jdlqog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus({ type: 'success', message: '感谢您的留言！我们会尽快回复您。' });
        setContactForm({ name: '', email: '', subject: '', message: '' });
      } else {
        const serverMsg =
          (parsed && (parsed as { meta?: { message?: string } }).meta?.message) ||
          (parsed && (parsed as { message?: string }).message) ||
          '提交失败，请稍后重试';
        setFormStatus({ type: 'error', message: serverMsg });
      }
    } catch {
      setFormStatus({ type: 'error', message: '网络错误，请检查网络连接后重试' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于小草影院 - 品牌介绍与联系方式',
            description: '了解小草影院的品牌故事、发展历程和联系方式。小草影院致力于为用户提供优质的电影在线观看服务。',
            url: 'https://xiaocaocinema.com/about',
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://xiaocaocinema.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: '关于我们',
                  item: 'https://xiaocaocinema.com/about',
                },
              ],
            },
          }),
        }}
      />

      <div className="min-h-screen pt-16">
        <nav className="px-4 md:px-6 py-4 bg-background-50 border-b border-background-200" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm">
            <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
            <span className="text-foreground-800 font-medium">关于我们</span>
          </div>
        </nav>

        <section className="py-12 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className="text-sm text-primary-500 font-medium">ABOUT US</span>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 mt-3 mb-5">
              关于小草影院
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              小草影院是深受广大影迷喜爱的在线电影平台，致力于为用户提供丰富、优质的院线大片和最新上映电影资源。我们以"让好电影触手可及"为使命，不断丰富片库内容。
            </p>
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="text-center p-8 bg-background-100 rounded-2xl">
              <div className="w-14 h-14 mx-auto mb-4 bg-primary-100 rounded-full flex items-center justify-center">
                <i className="ri-movie-2-line text-2xl text-primary-500"></i>
              </div>
              <h3 className="text-lg font-heading font-bold text-foreground-800 mb-2">海量片库</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                覆盖动作片、喜剧电影、科幻大片等十大分类，院线大片每日更新，满足各类观影需求。
              </p>
            </div>
            <div className="text-center p-8 bg-background-100 rounded-2xl">
              <div className="w-14 h-14 mx-auto mb-4 bg-accent-100 rounded-full flex items-center justify-center">
                <i className="ri-hd-line text-2xl text-accent-500"></i>
              </div>
              <h3 className="text-lg font-heading font-bold text-foreground-800 mb-2">高清画质</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                采用先进视频编码技术，提供高清在线观看体验，让您沉浸在影院级的视听享受中。
              </p>
            </div>
            <div className="text-center p-8 bg-background-100 rounded-2xl">
              <div className="w-14 h-14 mx-auto mb-4 bg-secondary-100 rounded-full flex items-center justify-center">
                <i className="ri-shield-check-line text-2xl text-secondary-500"></i>
              </div>
              <h3 className="text-lg font-heading font-bold text-foreground-800 mb-2">安全可靠</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                严格的内容审核和安全保障体系，为小草影院的每一位用户提供安全、舒适的观影环境。
              </p>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-sm text-accent-500 font-medium">我们的优势</span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-2">
                为什么选择小草影院
              </h2>
            </div>

            <div className="space-y-6">
              <div className="bg-background-50 rounded-2xl p-6 md:p-8">
                <h3 className="text-lg font-heading font-bold text-foreground-800 mb-3">
                  <i className="ri-film-line text-primary-500 mr-2"></i>
                  丰富的电影分类
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  小草影院提供十大电影分类，从紧张刺激的小草影院动作片到轻松愉快的小草影视喜剧电影，从烧脑的科幻大片到感人的爱情故事。无论你喜欢什么类型，都能在小草电影片库中找到心仪的影片。
                </p>
                <div className="flex flex-wrap gap-2">
                  {categories.filter(c => c.key !== 'all').map(cat => (
                    <Link
                      key={cat.key}
                      to={`/category/${cat.key}`}
                      className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-full hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 rounded-2xl p-6 md:p-8">
                <h3 className="text-lg font-heading font-bold text-foreground-800 mb-3">
                  <i className="ri-timer-flash-line text-accent-500 mr-2"></i>
                  每日更新片库
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  小草电影片库实行每日更新机制，第一时间收录院线大片和最新上映电影。我们的内容编辑团队持续追踪全球电影动态，确保小草影院的片库始终处于最新状态。院线电影高清在线观看，为您带来身临其境的观影体验。
                </p>
              </div>

              <div className="bg-background-50 rounded-2xl p-6 md:p-8">
                <h3 className="text-lg font-heading font-bold text-foreground-800 mb-3">
                  <i className="ri-user-heart-line text-secondary-500 mr-2"></i>
                  用户体验至上
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们始终把用户体验放在首位。简洁的界面设计、快速的搜索功能、智能的分类导航，让您在小草影院能够轻松找到想看的电影。支持多端观看，随时随地享受电影大全在线看的乐趣。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-sm text-primary-500 font-medium">口碑见证</span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-2">
                用户好评
              </h2>
              <p className="text-sm text-foreground-500 mt-2">来自小草影院用户的真实反馈</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-background-100 rounded-2xl p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-accent-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "小草影院的片库真的很全，我想看的动作片和科幻片都有。画质清晰，加载速度也快，已经推荐给身边的朋友了！"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center">
                    <span className="text-xs font-bold text-secondary-700">张</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">张先生</p>
                    <p className="text-xs text-foreground-500">影迷，使用小草影院1年</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-2xl p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-accent-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "小草影院的喜剧电影分类做得特别好，周末和家人一起看了几部新上的喜剧片，笑到停不下来！更新速度也很快。"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-200 flex items-center justify-center">
                    <span className="text-xs font-bold text-primary-700">李</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">李女士</p>
                    <p className="text-xs text-foreground-500">家庭主妇，使用小草影院6个月</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-2xl p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-accent-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "作为电影发烧友，小草影院是我每天必刷的平台。分类导航清晰，搜索功能好用，能找到很多小众好片，太赞了！"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-accent-200 flex items-center justify-center">
                    <span className="text-xs font-bold text-accent-700">王</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">王同学</p>
                    <p className="text-xs text-foreground-500">大学生，使用小草影院2年</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-sm text-accent-500 font-medium">CONTACT US</span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 mt-2">有任何问题或建议，欢迎随时与我们联系</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-background-50 rounded-2xl p-6">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mb-3">
                    <i className="ri-mail-line text-primary-500"></i>
                  </div>
                  <h4 className="text-sm font-bold text-foreground-800 mb-1">电子邮件</h4>
                  <p className="text-sm text-foreground-500">contact@xiaocaocinema.com</p>
                </div>

                <div className="bg-background-50 rounded-2xl p-6">
                  <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center mb-3">
                    <i className="ri-phone-line text-accent-500"></i>
                  </div>
                  <h4 className="text-sm font-bold text-foreground-800 mb-1">客服电话</h4>
                  <p className="text-sm text-foreground-500">400-888-8888</p>
                </div>

                <div className="bg-background-50 rounded-2xl p-6">
                  <div className="w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center mb-3">
                    <i className="ri-map-pin-line text-secondary-500"></i>
                  </div>
                  <h4 className="text-sm font-bold text-foreground-800 mb-1">公司地址</h4>
                  <p className="text-sm text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座15层</p>
                </div>
              </div>

              <div className="lg:col-span-3">
                <form onSubmit={handleSubmit} className="bg-background-50 rounded-2xl p-6 md:p-8" data-readdy-form="contact">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        className="w-full text-sm px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 text-foreground-700"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        className="w-full text-sm px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 text-foreground-700"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      name="subject"
                      value={contactForm.subject}
                      onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                      required
                      className="w-full text-sm px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 text-foreground-700"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div className="mb-5">
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      name="message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full text-sm px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 text-foreground-700 resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    />
                    <p className="text-xs text-foreground-400 mt-1 text-right">{contactForm.message.length}/500</p>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {formStatus && (
                    <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
                      formStatus.type === 'success'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      {formStatus.message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? '提交中...' : '发送留言'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}